// Error text that is always safe to render.
//
// Most routes answer with `detail: "<Arabic> / <English>"`, but some modules
// put an object there ({code, message_ar, message_en}, read by their own
// helpers such as wmError) and a FastAPI 422 is a list. React cannot render
// either: one object handed to toast.error() or to a banner blanks the app.
import { isValidElement } from "react";
import { toast } from "sonner";
import { translations } from "@/i18n";

function currentLang() {
    try {
        return document.documentElement.lang === "en" ? "en" : "ar";
    } catch (_e) {
        return "ar";
    }
}

const generic = () => translations[currentLang()]?.error_generic || translations.en.error_generic;

/** Any `detail` (string, number, object, list) -> a string. */
export function errorText(detail, fallback) {
    const fb = fallback ?? generic();
    if (detail === null || detail === undefined || detail === "") return fb;
    if (typeof detail === "string") return detail;
    if (typeof detail === "number") return String(detail);
    if (Array.isArray(detail)) {
        const parts = detail.map((d) => errorText(d, "")).filter(Boolean);
        return parts.length ? parts.join(" · ") : fb;
    }
    if (typeof detail === "object") {
        const ar = currentLang() === "ar";
        const msg = (ar ? detail.message_ar : detail.message_en)
            || detail.message_en || detail.message_ar || detail.message || detail.msg || detail.detail;
        if (typeof msg === "string" && msg) return msg;
    }
    return fb;
}

/** The text of an axios error (its `detail`), never an object. */
export const apiErrorText = (e, fallback) => errorText(e?.response?.data?.detail, fallback);

const renderable = (m) =>
    m === null || m === undefined || typeof m === "string" || typeof m === "number"
    || typeof m === "function" || isValidElement(m);

let guarded = false;

/** Every page calls `toast.error(e?.response?.data?.detail || …)` straight from
 *  sonner. Wrap the toast methods once at start-up so a non-renderable message
 *  or description becomes text instead of crashing the Toaster. */
export function guardToast() {
    if (guarded) return;
    guarded = true;
    for (const k of ["success", "error", "warning", "info", "message", "loading"]) {
        const orig = toast[k];
        if (typeof orig !== "function") continue;
        toast[k] = (message, data) => {
            const safeData = data && typeof data === "object" && "description" in data && !renderable(data.description)
                ? { ...data, description: errorText(data.description) }
                : data;
            return orig(renderable(message) ? message : errorText(message), safeData);
        };
    }
}
